import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import CgPopover from 'modules/Common/ui/CgPopover';
import ProjectedAverageInfo from 'modules/Common/ui/ProjectedAverageInfo';
import { formatNumber } from 'helpers/numberFormat';
import { useNumericText } from 'modules/Common/hooks/useNumericText';

const ControlWithTooltip = ({
  label,
  value,
  onChange,
  icon,
  unit,
  placeholder,
  tooltipTitle,
  tooltipOptions,
  inputWidth = 'w-12 lg:w-16',
  hideLabel = false,
}) => {
  // `onChange` receives a number, or null when cleared — never half-typed text.
  const [text, onText, resync] = useNumericText(value, onChange);
  const controlElement = (
    <div className="flex justify-center items-center space-x-1.5 lg:space-x-2">
      {!hideLabel && (
        <>
          <label className="text-xs font-medium text-gray-600 whitespace-nowrap hidden lg:inline">
            {label}:
          </label>
          <label className="text-xs font-medium text-gray-600 lg:hidden">
            {label?.split(' ')[0]}:
          </label>
        </>
      )}
      <div className="flex items-center gap-1 lg:gap-1.5 px-2 lg:px-2.5 py-1.5 bg-gray-50 hover:bg-gray-100 rounded-xl border border-gray-200 transition-colors">
        <FontAwesomeIcon
          icon={icon}
          className="text-primaryColor text-xs"
        />
        <input
          type="text"
          inputMode="decimal"
          value={text}
          onChange={(e) => onText(e.target.value)}
          // Left empty while the parent still holds a figure (a planning
          // scenario's price cannot be cleared): show that figure again.
          onBlur={() => String(text).trim() === '' && value != null && resync()}
          className={`${inputWidth} bg-transparent text-center focus:outline-none text-xs lg:text-sm font-medium`}
          placeholder={placeholder}
        />
        <span className="text-gray-500 text-xs whitespace-nowrap">{unit}</span>
      </div>
    </div>
  );

  const popoverContent = (
    <div className="w-90">
      <p className="text-xs lg:text-sm font-medium text-gray-800 mb-3">{tooltipTitle}:</p>
      <div className="space-y-2">
        {tooltipOptions.map((option) => (
          <button
            key={option.value}
            onClick={() => onChange(option.value)}
            className="w-full text-left p-2 hover:bg-gray-50 rounded-lg transition-colors"
          >
            <div className="flex justify-between items-center">
              <span className="text-sm font-medium flex items-center gap-1">
                {option.label}
                {option.isProjected && <ProjectedAverageInfo />}
              </span>
              <span className="text-sm text-primaryColor font-bold">
                {option.displayValue ||
                  `${formatNumber(option.value, { maximumFractionDigits: 4 })}${unit}`}
              </span>
            </div>
            {option.desc && <p className="text-xs text-gray-500 mt-1">{option.desc}</p>}
          </button>
        ))}
      </div>
    </div>
  );

  return (
    <CgPopover
      triggerElement={controlElement}
      popoverContent={popoverContent}
      position="bottom"
      trigger="hover"
      maxWidth="max-w-[300px]"
      sideOffset={10}
    />
  );
};

export default ControlWithTooltip;
